import React, { useMemo, useState } from 'react';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography
} from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import { Item, DataRow, UnitType, DenominatorType } from './types';
import { formatLocaleNumber } from '../../utils/string';
import { convertUnits } from './Chart.Common';

interface TotalCompoundsDisplayProps {
    items: Item<DataRow>[];
    selectedUnit: UnitType;
    selectedDenominator: DenominatorType;
}

const TotalCompoundsDisplay: React.FC<TotalCompoundsDisplayProps> = ({
                                                                         items,
                                                                         selectedUnit,
                                                                         selectedDenominator
                                                                     }) => {
    const [open, setOpen] = useState(false);

    const convertedItems = useMemo(() => {
        return convertUnits(items, selectedUnit, selectedDenominator);
    }, [items, selectedUnit, selectedDenominator]);

    const productTotals = useMemo(() => {
        const totalsByProduct = new Map<string, { totalCannabinoids: number, totalTerpenes: number }>();

        convertedItems.forEach(item => {
            item.DATA.forEach((row: DataRow) => {
                const productKey = `${row.PRODUCT || 'Unknown'} (${row.ANALYSIS_TYPE || 'Unknown'})`;

                if (!totalsByProduct.has(productKey)) {
                    totalsByProduct.set(productKey, { totalCannabinoids: 0, totalTerpenes: 0 });
                }

                let value = 0;

                if (typeof row.AVERAGE === 'number') {
                    value = row.AVERAGE;
                } else if (typeof row.AVERAGE === 'string') {
                    if (row.AVERAGE.includes('Q')) {
                        value = Number(row.LOQ) ?? 0;
                    } else if (row.AVERAGE.includes('D')) {
                        value = Number(row.LOD) ?? 0;
                    }
                }

                const totals = totalsByProduct.get(productKey)!;
                if (row.CLASS === 'Cannabinoids') {
                    totals.totalCannabinoids += value;
                } else if (row.CLASS === 'Terpenes') {
                    totals.totalTerpenes += value;
                }
            });
        });

        const result = Array.from(totalsByProduct.entries()).map(([product, totals]) => ({
            product,
            totalCannabinoids: totals.totalCannabinoids,
            totalTerpenes: totals.totalTerpenes
        })).sort((a, b) => a.product.localeCompare(b.product));

        console.log('TotalCompoundsDisplay - Product Totals:', result, 'Units:', `${selectedUnit}/${selectedDenominator}`);
        return result;
    }, [convertedItems]);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    return (
        <>
            <Button
                variant="outlined"
                color="inherit"
                onClick={handleOpen}
                startIcon={<BarChartIcon />}
                size="small"
                sx={{ ml: 'auto' }}
            >
                View Totals Cannabinoids + Terpenes
            </Button>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Totals by Product</DialogTitle>
                <DialogContent>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                        <thead>
                        <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <th style={{ padding: '8px', textAlign: 'left' }}>Product</th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>
                                Total Cannabinoids ({selectedUnit}/{selectedDenominator})
                            </th>
                            <th style={{ padding: '8px', textAlign: 'right' }}>
                                Total Terpenes ({selectedUnit}/{selectedDenominator})
                            </th>
                        </tr>
                        </thead>
                        <tbody>
                        {productTotals.length > 0 ? (
                            productTotals.map(({ product, totalCannabinoids, totalTerpenes }) => (
                                <tr key={product} style={{ borderBottom: '1px solid #eee' }}>
                                    <td style={{ padding: '8px' }}>{product}</td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(totalCannabinoids)}</td>
                                    <td style={{ padding: '8px', textAlign: 'right' }}>{formatLocaleNumber(totalTerpenes)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={3} style={{ padding: '8px', textAlign: 'center' }}>
                                    No data available
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TotalCompoundsDisplay;
